import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../shared/components';
import { examWeighting, sourceMaterials, examStructure, backgroundNote, subjectMatters } from './content/overview';
import './StudyGuide.css';

const topicGuides = [
  {
    to: '/study-guide/economic-factors',
    title: 'Economic Factors and Business Information',
    description: 'Business cycles, monetary and fiscal policy, financial reporting, analytical methods, and types of risk — exam section 2.1.',
  },
  {
    to: '/study-guide/investment-vehicles',
    title: 'Investment Vehicle Characteristics',
    description: 'Cash equivalents, fixed income, equity, pooled investments, derivatives, alternatives, and insurance-based products — exam section 2.2.',
  },
  {
    to: '/study-guide/client-recommendations',
    title: 'Client Investment Recommendations and Strategies',
    description: 'Client profiling, capital market theory, portfolio management, tax and retirement planning, and performance measures — exam section 2.3.',
  },
  {
    to: '/study-guide/laws-and-regulations',
    title: 'Laws, Regulations and Guidelines Including Prohibition on Unethical Business Practices',
    description: 'What exam section 2.4 covers that isn’t already the Uniform Securities Act, the Investment Advisers Act, or the Model Rules below.',
  },
];

const primarySources = [
  {
    to: '/study-guide/uniform-securities-act',
    title: 'Uniform Securities Act (1956), as amended',
    description: 'The state-law backbone of the exam — read the full text and highlight what you need to remember.',
  },
  {
    to: '/study-guide/investment-advisers-act',
    title: 'Investment Advisers Act of 1940',
    description: 'The federal law source for the exam — registration, fiduciary duty, and antifraud provisions.',
  },
  {
    to: '/study-guide/model-rules',
    title: 'NASAA Model Rules',
    description: 'Unethical business practices, custody, recordkeeping, and the other model rules named in the exam outline.',
  },
];

export default function StudyGuide() {
  return (
    <div className="study-guide-page">
      <h1>Study Guide</h1>
      <p className="study-guide-intro">
        This overview reproduces NASAA's official "Series 65 Exam Content Outline" — what's actually tested, and how much of
        the exam each subject covers. Use it to plan your studying, then dig into the topic guides and primary source law below.
      </p>

      <h2 className="study-guide-group-heading">Topic-by-Topic Study Guides</h2>
      <p className="study-guide-group-subheading">
        Original study content, one document per official exam section — the concepts and vocabulary the exam tests, not
        legal text.
      </p>
      <div className="study-guide-links study-guide-links--small study-guide-links--four">
        {topicGuides.map((page) => (
          <Link key={page.to} to={page.to} className="study-guide-link-card">
            <Card className="study-guide-card study-guide-card--topic study-guide-card--small">
              <span className="study-guide-card-badge study-guide-card-badge--topic">Topic Guide</span>
              <h2>{page.title}</h2>
              <p>{page.description}</p>
            </Card>
          </Link>
        ))}
      </div>

      <h2 className="study-guide-group-heading">Primary Source Law</h2>
      <p className="study-guide-group-subheading">
        The actual statutes and model rules the exam is built on, readable and highlightable in full.
      </p>
      <div className="study-guide-links study-guide-links--small study-guide-links--three">
        {primarySources.map((page) => (
          <Link key={page.to} to={page.to} className="study-guide-link-card">
            <Card className="study-guide-card study-guide-card--source study-guide-card--small">
              <span className="study-guide-card-badge study-guide-card-badge--source">Primary Source</span>
              <h2>{page.title}</h2>
              <p>{page.description}</p>
            </Card>
          </Link>
        ))}
      </div>

      <section className="study-guide-section">
        <h2>About the exam</h2>
        <p>{backgroundNote}</p>
        <ul>
          {examStructure.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section className="study-guide-section">
        <h2>What the exam draws from</h2>
        <p>{sourceMaterials.general}</p>
        <p><strong>Federal law:</strong> {sourceMaterials.federalLaw.join(' • ')}</p>
        <p><strong>State law:</strong> {sourceMaterials.stateLaw.join(' • ')}</p>
      </section>

      <section className="study-guide-section">
        <h2>Subject-matter weighting</h2>
        <table className="study-weight-table">
          <thead>
            <tr>
              <th>Subject matter</th>
              <th>Weight</th>
              <th># Questions</th>
            </tr>
          </thead>
          <tbody>
            {examWeighting.map((row) => (
              <tr key={row.subject}>
                <td>{row.subject}</td>
                <td>{row.weight}%</td>
                <td>{row.questions}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: '#999999' }}>
          Each exam includes 140 questions, of which 130 are scored (the other 10 are unscored pre-test questions).
        </p>
      </section>

      {subjectMatters.map((subject) => (
        <section className="study-guide-section" key={subject.id}>
          <h2>{subject.title}</h2>
          <p className="study-guide-intro" style={{ marginBottom: 12 }}>{subject.weight}</p>
          {subject.components.map((component) => (
            <div className="study-subject-block" key={component.heading}>
              <p className="study-component-heading">{component.heading}</p>
              <ul className="study-component-list">
                {component.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
